<script setup lang="ts">

import { ref, watch } from 'vue'

import { Swiper, SwiperSlide } from 'swiper/vue'
import { FreeMode, Thumbs, Pagination, Navigation, Keyboard } from 'swiper/modules'
import { Image } from '../interfaces/listing'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/free-mode'
import 'swiper/css/thumbs'

import type { Swiper as SwiperInstance } from 'swiper/types'
import { Breakpoints, PaginationOptions } from '../interfaces/listingSwiper'

type Props = {
    listingImages: Image[];
    class?: string,
    navigationMain?: boolean
    paginationMain?: PaginationOptions
    spaceBetweenThumb?: number 
    breakpointsThumb: Breakpoints
    initialSlide?: number
}

const props = defineProps<Props>()

const thumbsSwiper = ref<SwiperInstance>()

const onSwiper = (swiper: SwiperInstance): void => {
    thumbsSwiper.value = swiper
}

const modules = [FreeMode, Thumbs, Pagination, Navigation, Keyboard]

watch(() => props.initialSlide, (newSlide) => {
    if (newSlide !== undefined && thumbsSwiper.value) {
        thumbsSwiper.value.slideTo(newSlide, 0, false)
    }
})

</script>

<template>
    <div :class="[props.class]">
        <Swiper
            :space-between="10"
            :thumbs="{ swiper: thumbsSwiper }"
            :pagination="paginationMain"
            :modules="modules"
            :keyboard="{enabled: true}"
            class="listing-swiper"
            centered-slides
            centered-slides-bounds
            grab-cursor
            lazy
            :navigation="navigationMain"
            :initial-slide="props.initialSlide"
        >
            <SwiperSlide v-for="(slide, index) in listingImages" :key="index">
                <picture>
                    <source :srcset="slide.src.big_webp" type="image/webp">
                    <source :srcset="slide.src.big" type="image/jpg">

                    
                    <img
                        :alt="slide.title"
                        :title="slide.title"
                        loading="lazy"
                    >
                </picture>
                <div class="swiper-lazy-preloader" />
            </SwiperSlide>
        </Swiper>
        <Swiper
            :space-between="spaceBetweenThumb"
            :free-mode="true"
            :watch-slides-progress="true"
            :modules="modules"
            class="listing-swiper-thumb"
            :breakpoints="breakpointsThumb"
            grab-cursor
            lazy
            @swiper="onSwiper"
        >
            <SwiperSlide v-for="(slide, index) in listingImages" :key="index">
                <picture>
                    <source :srcset="slide.src.thumb_webp" type="image/webp">
                    <source :srcset="slide.src.thumb_def" type="image/jpg">

                    <img
                        :alt="slide.title"
                        :title="slide.title"
                        loading="lazy"
                    >
                </picture>
                <div class="swiper-lazy-preloader" />
            </SwiperSlide>
        </Swiper>
    </div>
</template>

<style lang="scss" scoped>
.listing-swiper {
    img {
        object-fit: cover;
        width: 100%; 
        height: 100%;

        @media (min-width: 768px) {
            object-fit: contain;
        }
    }

    &-thumb {
        img {
            object-fit: cover;
            width: 100%; 
            height: 100%;
        }
    }
}

.listing-swiper .swiper-lazy-preloader {
    border-color: var(--primary-brand);
    border-top-color: transparent;
}

.listing-swiper-thumb .swiper-lazy-preloader {
    border-color: var(--primary-brand);
    border-top-color: transparent;
}
</style>